import { Box, Typography } from '@mui/material';
import { AppConfig } from 'AppConfig';
import Carousel from 'common/bstCarousel';
import * as Constant from 'common/Constant';
import * as ReportActions from 'features/frontoffice/actions/actions';
import * as UserTypes from 'features/user/userTypes';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { store } from 'store/store';
import ReportFilters from '../component/reportFilters';
import * as Action from '../favoriteReport/actions/action';
import NoAvailableReports from 'features/frontoffice/component/noAvailableReports';
import { sortReports } from 'utils/reportSort';
import ReportSectionLoading from '../component/reportSectionLoading';

const StandardReport = ({ selectedFilterTags: selectedFilterTagsProp = [], isMobile = false, isTablet = false, searchTerm = '', sortBy, viewMode = 'grid' }) => {
  const legacies = useSelector((state) => state.fo.legacies);
  const legaciesLoading = useSelector((state) => state.fo.legaciesLoading);
  const reportTags = useSelector((state) => state.fo.reportTags);
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const currentMenu = useSelector((state) => state.favoriteReport.currentMenu);
  const persistedTags = useSelector((state) => state.favoriteReport.selectedLegacyTags);

  const sortKey = typeof sortBy === 'string' ? sortBy : sortBy?.value;

  const sortedLegacies = useMemo(() => {
    const filteredLegacies = sortKey === 'recently_saved' ? legacies?.filter((l) => l.favorited === true) : legacies;
    return sortReports(filteredLegacies, sortKey);
  }, [legacies, sortKey]);
  const [data, setData] = useState(sortedLegacies);
  const [searchKey, setSearchKey] = useState('');
  // Selected tags derived from persisted codes; stays in sync as tags load/change.
  const selectedFilterTags = useMemo(() => {
    if (!Array.isArray(reportTags) || !Array.isArray(persistedTags) || persistedTags.length === 0) return [];
    return _.sortBy(reportTags.filter((t) => persistedTags.includes(t?.code)), (t) => t.order);
  }, [reportTags, persistedTags]);
  const selectedMobileFilterTags = Array.isArray(selectedFilterTagsProp) ? selectedFilterTagsProp : [];

  const searchedLegacies = useMemo(() => {
    const list = Array.isArray(sortedLegacies) ? sortedLegacies : [];
    const term = (searchTerm || '').trim().toLowerCase();
    if (!term) return list;
    return list.filter((item) => {
      const title = (item?.displayText || '').toLowerCase();
      const desc = (item?.description || '').toLowerCase();
      return title.includes(term) || desc.includes(term);
    });
  }, [sortedLegacies, searchTerm]);

  // App context + initial data
  useEffect(() => {
    store.dispatch({ type: UserTypes.APP_SWITCHED, payload: AppConfig.HEADER_MENU_TYPE.FO });
    store.dispatch(ReportActions.getStandardReport());
  }, [currentTenantCd]);

  // Ensure tags exist even if ReportFilters isn't mounted (e.g., on mobile)
  useEffect(() => {
    if (!Array.isArray(reportTags) || reportTags.length === 0) {
      store.dispatch(ReportActions.fetchReportTags());
    }
  }, [currentTenantCd, reportTags]);

  // Default menu = All, but keep a saved category selection so ReportFilters can restore it.
  useEffect(() => {
    const hasPersisted = Array.isArray(persistedTags) && persistedTags.length > 0;
    store.dispatch(Action.setCurrentMenuItem({ name: hasPersisted ? null : Constant.ALL }));
  }, [currentTenantCd]);

  // Search helpers (for non light-touch flow)
  const searchKeyLC = (searchKey || '').toLowerCase();
  const searchFilter = (value) => {
    if (Array.isArray(value)) {
      const newData = _.filter(value, (o) => {
        const title = (o?.displayText || '').toLowerCase();
        const desc = (o?.description || '').toLowerCase();
        return title.includes(searchKeyLC) || desc.includes(searchKeyLC);
      });
      setData(sortReports(newData, sortKey));
    }
  };

  const filter = (value) => {
    if (!Array.isArray(sortedLegacies)) return;
    if (value === Constant.ALL) {
      searchFilter(sortedLegacies);
      return;
    }
    const newData = _.filter(sortedLegacies, (o) =>
      o.reportTag?.name ? o.reportTag?.name?.toLowerCase().includes(value?.toLowerCase()) : false
    );
    searchFilter(newData);
  };

  useEffect(() => {
    // keep state of searched data while navigating ReportNav
    filter(currentMenu?.name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyLC]);

  useEffect(() => {
    if (!searchTerm) {
      setSearchKey('');
    }
  }, [currentMenu, searchTerm]);

  useEffect(() => {
    setSearchKey(searchTerm || '');
  }, [searchTerm]);

  // Sort tags once
  const sortedReportTags = useMemo(
    () => (Array.isArray(reportTags) ? _.sortBy(reportTags, (t) => t.order) : []),
    [reportTags]
  );

  // Helper: get items per tag
  const itemsForTag = (tag) =>
    Array.isArray(searchedLegacies) ? searchedLegacies.filter((o) => o?.reportTag?.code === tag?.code) : [];

  // Renders sections for a given tag selection
   const renderTagSections = (tagsSelected) => {
    const isAll = !Array.isArray(tagsSelected) || tagsSelected.length === 0;

    if (isAll && (!Array.isArray(searchedLegacies) || searchedLegacies.length === 0)) {
      return <NoAvailableReports />;
    }

    if (isAll) {
      return sortedReportTags.map((tag) => {
        const items = itemsForTag(tag);
        if (!items.length) return null;
        return (
          <Box key={tag.id} sx={{ mb: '24px', mt: '24px' }}>
            <Typography
              variant="h2"
              sx={{
                fontFamily: 'Rubik',
                fontSize: '18px',
                fontStyle: 'normal',
                fontWeight: 500,
                lineHeight: '27px',
                color: '#000000',
                mb: '24px'
              }}>
              {tag?.name ?? 'Unnamed'} ({items.length})
            </Typography>
            <Carousel items={items} type={AppConfig.LEGACY_CAROUSEL} isTablet={isTablet} viewMode={viewMode} />
          </Box>
        );
      });
    }

    return tagsSelected.map((tag) => {
      const items = itemsForTag(tag);
      return (
        <Box key={tag.id} sx={{ mb: '24px', mt: '24px' }}>
          <Typography
            variant="h2"
            sx={{
              fontFamily: 'Rubik',
              fontSize: '18px',
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '27px',
              color: '#000000',
              mb: '24px'
            }}>
            {tag?.name ?? 'Unnamed'}
            {tag?.code ? ` (${items.length})` : ''}
          </Typography>

          {items.length ? <Carousel items={items} type={AppConfig.LEGACY_CAROUSEL} isTablet={isTablet} viewMode={viewMode} /> : <NoAvailableReports />}
        </Box>
      );
    });
  };

  if (isMobile) {
    // Mobile
      if (legaciesLoading) return <ReportSectionLoading count={6} variant="grid" />;
      return <>{renderTagSections(selectedMobileFilterTags)}</>;
  
    // return renderClassicLayout();
  }

  // Desktop / Tablet
 
    return (
      <>
       {legaciesLoading ? (
        <ReportSectionLoading count={10} variant="grid" />
      ) :
        !Array.isArray(legacies) || !legacies.length ? (
          <NoAvailableReports />
        ) : (
          <>
            {searchedLegacies.length > 0 && <Box sx={{ mb: 3 }}>
              <ReportFilters setData={setData} filter={filter} data={data} persistSelection />
            </Box>}
            <Box sx={{display:'flex', flexDirection:'column'}}>
             {renderTagSections(selectedFilterTags)}
            </Box>
          </>
        )}
      </>
    );
  

  // return renderClassicLayout();
};

export default StandardReport;
